import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Inbox, FileDown, RefreshCw } from "lucide-react";

const STATUS = { new: ["Nuova", "bg-amber-500/15 text-amber-300 ring-amber-500/30"], contacted: ["Contattata", "bg-sky-500/15 text-sky-300 ring-sky-500/30"], closed: ["Chiusa", "bg-zinc-700 text-zinc-300 ring-zinc-600"] };
const BROCHURE = {
  pending: ["In invio…", "bg-zinc-800 text-zinc-400"], none: ["Non inviata", "bg-zinc-800 text-zinc-400"], sent: ["Inviata", "bg-sky-500/15 text-sky-300"], delivered: ["Consegnata", "bg-emerald-500/15 text-emerald-300"],
  opened: ["Aperta", "bg-emerald-500/20 text-emerald-200"], clicked: ["Link cliccato", "bg-emerald-500/25 text-emerald-100"],
  bounced: ["Bounce", "bg-red-500/15 text-red-300"], failed: ["Fallita", "bg-red-500/15 text-red-300"], complained: ["Segnalata spam", "bg-red-500/15 text-red-300"],
  suppressed: ["Indirizzo soppresso", "bg-red-500/15 text-red-300"], error: ["Errore invio", "bg-red-500/15 text-red-300"],
};
const fmt = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "");

function BrochureBadge({ r, onResend, busy }) {
  const b = r.brochure || { state: "pending" };
  const [label, cls] = BROCHURE[b.state] || BROCHURE.pending;
  const bad = ["bounced", "failed", "complained", "suppressed", "error"].includes(b.state);
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[11px]" data-testid={`demo-brochure-${r.id}`}>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${cls}`} title={b.error || ""} data-testid={`demo-brochure-state-${r.id}`}><FileDown className="h-3 w-3" /> Brochure: {label}</span>
      {b.sent_at && <span className="text-zinc-500">{fmt(b.sent_at)}{b.sends > 1 ? ` · ${b.sends} invii` : ""}</span>}
      {bad && b.error && <span className="text-red-300/80 truncate max-w-[260px]" title={b.error}>{b.error}</span>}
      <button type="button" disabled={busy} onClick={() => onResend(r.id)} className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2 py-0.5 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50" data-testid={`demo-brochure-resend-${r.id}`}>
        <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Invio…" : "Reinvia"}
      </button>
    </div>
  );
}

// Richieste demo arrivate dalla landing (Super-Admin) + stato consegna brochure PDF
export default function DemoRequests() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/platform/demo-requests").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.patch(`/platform/demo-requests/${id}`, { status }); toast.success("Stato aggiornato"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const resend = async (id) => {
    setBusy(id);
    try { await api.post(`/platform/demo-requests/${id}/resend-brochure`); toast.success("Brochure reinviata"); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  if (!rows) return null;
  const open = rows.filter((r) => r.status === "new").length;
  return (
    <div className="card-elev p-4 space-y-3" data-testid="demo-requests">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Inbox className="h-3.5 w-3.5" /> Richieste demo dalla landing {open > 0 && <span className="ml-1 rounded-full bg-amber-500 text-black px-1.5 font-bold" data-testid="demo-requests-open">{open}</span>}</div>
      {rows.length === 0 ? <p className="text-xs text-zinc-500" data-testid="demo-requests-empty">Nessuna richiesta ricevuta.</p> : (
        <div className="divide-y divide-zinc-800">
          {rows.slice(0, 30).map((r) => (
            <div key={r.id} className="py-2.5 flex flex-wrap items-start justify-between gap-2 text-sm" data-testid={`demo-request-${r.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-zinc-100">{r.company} <span className="font-normal text-zinc-400">· {r.name}</span></div>
                <div className="text-xs text-zinc-400"><a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a> · <a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a> · {fmt(r.at)}</div>
                {r.message && <div className="text-xs text-zinc-300 mt-1 whitespace-pre-line">{r.message}</div>}
                <BrochureBadge r={r} onResend={resend} busy={busy === r.id} />
              </div>
              <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className={`text-xs rounded-full px-2 py-1 ring-1 bg-transparent ${STATUS[r.status]?.[1] || ""}`} data-testid={`demo-request-status-${r.id}`}>
                {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k} className="bg-zinc-900 text-zinc-100">{l}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
